import type {
    CSSProperties,
    ElementType,
    HTMLAttributes,
    ReactNode
} from 'react'

import { useRef } from 'react'
import { useInView } from '@/hooks/use-in-view'
import { useMounted } from '@/hooks/use-mounted'

interface RevealProps extends Omit<HTMLAttributes<HTMLElement>, 'children' | 'className' | 'style'> {
    as?: ElementType
    children?: ReactNode
    className?: string
    delay?: number
    x?: number
    y?: number
    blur?: number
    scale?: number
    trigger?: 'view' | 'mount'
    style?: CSSProperties
}

/**
 * Fades and lifts its children into place when they scroll into view
 * (or right after mount). Motion is skipped for reduced-motion visitors.
 */
export const Reveal = ({
    as: Tag = 'div',
    children,
    className,
    delay = 0,
    x = 0,
    y = 28,
    blur = 0,
    scale = 1,
    trigger = 'view',
    style,
    ...rest
}: RevealProps) => {
    const ref = useRef<HTMLElement>(null)
    const inView = useInView(ref)
    const mounted = useMounted()
    const shown = trigger === 'mount' ? mounted : inView

    return (
        <Tag
            {...rest}
            ref={ref}
            data-reveal=''
            data-shown={shown ? '' : undefined}
            className={className}
            style={{
                '--reveal-delay': `${delay}ms`,
                '--reveal-x': `${x}px`,
                '--reveal-y': `${y}px`,
                '--reveal-blur': `${blur}px`,
                '--reveal-scale': scale,
                ...style
            } as CSSProperties}
        >
            {children}
        </Tag>
    )
}
